import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'underscore';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withUiBlock } from '@bingads-webui/uiblocker';

import { AudienceActions } from './audience-actions';
import { AudienceCard } from './audience-card';
import { AudienceEditor } from './audience-editor';
import { AudienceSelector } from './audience-selector';
import { fetchAudienceGroup, normalizeModelForAudienceGroup } from '../model/audience-group';
import { getAudienceWizardStyle } from '../styles/audience-wizard';
import { GENERIC_ERROR_MESSAGE_KEY } from '../constants/strings';

const UnstyledAudienceWizard = (props) => {
  const {
    audienceGroup,
    originalAudienceGroup,
    isReadOnlyUser,
    aid,
    cid,
    classes,
    i18n,
    updateValidation,
    wizardPerfMarker,
  } = props;

  const wizardRef = useRef();

  const [showAudienceEditor, setShowAudienceEditor] = useState(false);
  const [showAudienceSelector, setShowAudienceSelector] = useState(false);

  const [selectedAudienceGroup, setSelectedAudienceGroup] = useState(audienceGroup);

  const onGenericError = useCallback(() => {
    const alertHelper = new AlertHelper({ i18n });
    alertHelper.showError(i18n.getString(GENERIC_ERROR_MESSAGE_KEY));
  }, [i18n]);

  const onAudienceGroupChanged = useCallback((newAudienceGroup) => {
    setSelectedAudienceGroup(newAudienceGroup);

    if (newAudienceGroup && !newAudienceGroup.Status) {
      fetchAudienceGroup({ aid, cid, audienceGroupId: newAudienceGroup.Id })
        .then(updatedAudienceGroup => setSelectedAudienceGroup(normalizeModelForAudienceGroup(updatedAudienceGroup)))
        .catch(onGenericError)
        .finally(withUiBlock(wizardRef.current));
    }
  }, [aid, cid, onGenericError]);

  const onBrowseAudienceSignal = useCallback(() => {
    setShowAudienceSelector(true);
  }, []);

  const onCreateAudienceSignal = useCallback(() => {
    setShowAudienceEditor(true);
  }, []);

  const onEditAudienceSignal = useCallback(() => {
    setShowAudienceEditor(true);
  }, []);

  const onRemoveAudienceSignal = useCallback(() => {
    onAudienceGroupChanged(null);
  }, [onAudienceGroupChanged]);

  useEffect(() => {
    // AudienceGroup will always be valid no matter selected or not
    updateValidation([], { AudienceGroup: selectedAudienceGroup });
  }, [selectedAudienceGroup, updateValidation]);

  return (
    <div className={classNames(['audience-wizard', classes.wizardContainer])} ref={wizardRef}>
      {!selectedAudienceGroup && (
        <AudienceActions
          i18n={i18n}
          onBrowseAudienceSignal={onBrowseAudienceSignal}
          onCreateAudienceSignal={onCreateAudienceSignal}
        />
      )}
      {selectedAudienceGroup && (
        <AudienceCard
          audienceGroup={selectedAudienceGroup}
          i18n={i18n}
          isReadOnlyUser={false}
          onEditAudienceSignal={onEditAudienceSignal}
          onRemoveAudienceSignal={onRemoveAudienceSignal}
          showActions
        />
      )}
      {showAudienceEditor && (
        <AudienceEditor
          audienceGroup={selectedAudienceGroup}
          originalAudienceGroup={originalAudienceGroup}
          i18n={i18n}
          isOpen={showAudienceEditor}
          isReadOnlyUser={isReadOnlyUser}
          onAudienceGroupChanged={onAudienceGroupChanged}
          onClose={() => setShowAudienceEditor(false)}
          onGenericError={onGenericError}
          wizardPerfMarker={wizardPerfMarker}
        />
      )}
      {showAudienceSelector && (
        <AudienceSelector
          aid={aid}
          cid={cid}
          i18n={i18n}
          isOpen={showAudienceSelector}
          onAudienceGroupChanged={onAudienceGroupChanged}
          onEditAudienceSignal={onEditAudienceSignal}
          onGenericError={onGenericError}
          onClose={() => setShowAudienceSelector(false)}
          originalAudienceGroup={originalAudienceGroup}
          wizardPerfMarker={wizardPerfMarker}
        />
      )}
    </div>
  );
};

UnstyledAudienceWizard.propTypes = {
  audienceGroup: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    Dimensions: PropTypes.arrayOf(PropTypes.shape({
      Type: PropTypes.string.isRequired,
    })).isRequired,
  }),
  originalAudienceGroup: PropTypes.shape({
    Id: PropTypes.number.isRequired,
  }),
  isReadOnlyUser: PropTypes.bool.isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  classes: PropTypes.shape({
    wizardContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  wizardPerfMarker: PropTypes.shape({}).isRequired,
};

UnstyledAudienceWizard.defaultProps = {
  audienceGroup: null,
  originalAudienceGroup: null,
};

export const AudienceWizard = compose(
  WrappedComponent => withDefaultStyles(WrappedComponent, getAudienceWizardStyle),
  withCustomValidation
)(UnstyledAudienceWizard);
